import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { Device } from '../../constants/device';
import { AppLanguage } from '../../model/enums/AppLanguage';
import { Colors } from '../../constants/colors';
import { useTranslation } from 'react-i18next';
import { ArrowDownIcon } from '../../components/icons/ArrowDownIcon';
import { StyledMenuLink } from '../../components/StyledMenuLink';

export const LanguageSwitcher: React.FC = () => {
  const { pathname } = useLocation();
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  useEffect(() => {
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  const switchPath = (language: string) => {
    if (/^\/[a-z]{2}(\/|$)/.test(pathname)) {
      return pathname.replace(/^\/[a-z]{2}/, `/${language}`);
    }
    return `/${language}${pathname}`;
  };

  return (
    <SwitcherWrapper ref={ref}>
      <ToggleButton href={'#'} color={Colors.primary} onClick={() => setOpen((o) => !o)}>
        {i18n.language}
        <ArrowDownIcon size={20} />
      </ToggleButton>
      <Dropdown open={open}>
        {Object.values(AppLanguage).map((lang) => {
          const active = lang === i18n.language;
          return (
            <MenuItem key={lang} active={active}>
              <Link
                to={switchPath(lang)}
                onClick={() => {
                  rememberLanguage(lang);
                  i18n.changeLanguage(lang);
                  setOpen(false);
                }}
              >
                {lang}
              </Link>
            </MenuItem>
          );
        })}
      </Dropdown>
    </SwitcherWrapper>
  );
};

// A language picked here wins over the browser preferences in the language
// detection in public/index.html.
const rememberLanguage = (lang: AppLanguage) => {
  try {
    localStorage.setItem('slp.language', lang);
  } catch {
    // Storage blocked, the browser preferences keep deciding.
  }
};

const SwitcherWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const ToggleButton = styled(StyledMenuLink)`
  display: flex;
  align-items: center;
  gap: 5px;
`;

const Dropdown = styled.ul<{ open: boolean }>`
  display: ${({ open }) => (open ? 'block' : 'none')};
  position: absolute;
  top: 100%;
  right: 0;
  margin: 10px 0 0;
  padding: 0;
  background: ${Colors.white};
  border: 1px solid ${Colors.border};
  list-style: none;
  z-index: 10;

  ${Device.desktop} {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  ${Device.mobile} {
    display: ${({ open }) => (open ? 'flex' : 'none')};
    left: 50%;
    right: unset;
    transform: translateX(-50%);
  }
`;

const MenuItem = styled.li<{ active: boolean }>`
  a {
    display: block;
    padding: 0.5rem 1rem;
    color: ${Colors.primary};
    text-decoration: none;
    font-weight: ${({ active }) => (active ? '700' : '400')};

    &:hover {
      background: ${Colors.lighterGrey};
    }
  }
`;
